import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, CornerDownLeft } from "lucide-react";
import api from "../lib/api";

const CATEGORY_LABEL = {
  responsibilities: "Records", pets: "Pets", vehicles: "Vehicles", properties: "Properties",
  documents: "Documents", reminders: "Reminders", trips: "Trips", passports: "Passports",
  members: "Household", notes: "Notes", community: "Community", articles: "Articles",
  resource_centre: "Resource Centre", guides: "Guides", faqs: "FAQs", policies: "Policies",
  estate: "Estate Hub", baby: "Baby Hub", budgets: "Budgets", pages: "Go to", academy: "Academy",
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); setTotal(0); setOpen(false); return; }
    setLoading(true); setOpen(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data.results || []); setTotal(data.total || 0);
      } catch { setResults([]); setTotal(0); }
      finally { setLoading(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const go = (url) => { setOpen(false); setQ(""); navigate(url); };
  const seeAll = () => { if (q.trim().length >= 2) go(`/app/search?q=${encodeURIComponent(q.trim())}`); };
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); seeAll(); }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative mb-3" data-testid="global-search">
      <div className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 focus-within:border-[#0F766E]">
        <Search className="h-4 w-4 shrink-0 text-[#9aa3a0]" strokeWidth={1.8} />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => q.trim().length >= 2 && setOpen(true)}
          placeholder="Search PlannioLife…"
          aria-label="Search PlannioLife"
          className="w-full bg-transparent text-sm text-[#2D3436] outline-none placeholder:text-[#9aa3a0]"
        />
        {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#9aa3a0]" />}
      </div>

      {open && (
        <div data-testid="global-search-dropdown" className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-[#E2E8F0] bg-white py-1.5 shadow-xl">
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-[#9aa3a0]" data-testid="global-search-empty">No results for “{q.trim()}”.</div>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              data-testid={`global-search-result-${i}`}
              onClick={() => go(r.url)}
              className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-[#F4F6F2]"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-[#2D3436]">{r.title}</span>
                <span className="block truncate text-xs text-[#9aa3a0]">{CATEGORY_LABEL[r.category] || r.category}{r.subtitle ? ` · ${r.subtitle}` : ""}</span>
              </span>
            </button>
          ))}
          {total > 0 && (
            <button data-testid="global-search-see-all" onClick={seeAll} className="mt-1 flex w-full items-center justify-between border-t border-[#E2E8F0] px-4 py-2.5 text-sm font-medium text-[#0F766E] hover:bg-[#0F766E]/5">
              <span>See all {total} result{total === 1 ? "" : "s"}</span>
              <CornerDownLeft className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
